import React, { useMemo } from 'react';
import { Transaction, Period } from '../types';
import { TransactionList } from '../components/TransactionList';
import { StatCard } from '../components/StatCard';
import { Wallet, TrendingUp } from 'lucide-react';
import { PeriodToggle } from '../components/PeriodToggle';
import { DateNavigation } from '../components/DateNavigation';
import { isSameDay, isSameMonth, isSameYear } from 'date-fns';
interface SavingsPageProps {
  transactions: Transaction[];
  period: Period;
  date: Date;
  onPeriodChange: (p: Period) => void;
  onDateChange: (d: Date) => void;
  onEditTransaction: (t: Transaction) => void;
}
export function SavingsPage({
  transactions,
  period,
  date,
  onPeriodChange,
  onDateChange,
  onEditTransaction
}: SavingsPageProps) {
  const savingsTransactions = useMemo(() => {
    return transactions.filter(t => t.type === 'savings');
  }, [transactions]);
  const totalSavings = useMemo(() => {
    return savingsTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [savingsTransactions]);
  const filteredSavings = useMemo(() => {
    return savingsTransactions.filter(t => {
      const tDate = new Date(t.date);
      if (period === 'day') return isSameDay(tDate, date);
      if (period === 'month') return isSameMonth(tDate, date);
      return isSameYear(tDate, date);
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [savingsTransactions, period, date]);
  const handlePrevDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() - 1);
    if (period === 'month') newDate.setMonth(date.getMonth() - 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() - 1);
    onDateChange(newDate);
  };
  const handleNextDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() + 1);
    if (period === 'month') newDate.setMonth(date.getMonth() + 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() + 1);
    onDateChange(newDate);
  };
  return <div className="space-y-6 pb-24" data-id="element-463">
      <h1 className="text-2xl font-bold text-stone-50 px-2" data-id="element-464">Savings Goals</h1>

      <div className="bg-gradient-to-br from-emerald-900 to-stone-900 rounded-3xl p-6 border border-emerald-900/50 shadow-xl relative overflow-hidden" data-id="element-465">
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-466" />
        <div className="relative z-10" data-id="element-467">
          <div className="flex items-center gap-2 mb-2" data-id="element-468">
            <Wallet className="text-emerald-500" size={24} data-id="element-469" />
            <span className="text-emerald-200/70 text-sm uppercase font-bold tracking-wider" data-id="element-470">
              Total Saved
            </span>
          </div>
          <p className="text-4xl font-bold text-white" data-id="element-471">
            ${totalSavings.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="bg-stone-900 sticky top-0 z-30 py-2 border-b border-stone-800/50 backdrop-blur-md bg-opacity-80" data-id="element-472">
        <PeriodToggle value={period} onChange={onPeriodChange} data-id="element-473" />
        <DateNavigation date={date} period={period} onPrev={handlePrevDate} onNext={handleNextDate} onDateClick={() => {}} onDateChange={onDateChange} data-id="element-474" />
      </div>

      <div data-id="element-475">
        <h3 className="text-stone-400 text-sm font-bold uppercase tracking-wider mb-3 px-2" data-id="element-476">
          History
        </h3>
        <TransactionList transactions={filteredSavings} onEdit={onEditTransaction} emptyMessage="No savings records for this period" data-id="element-477" />
      </div>
    </div>;
}